import { isRegion, Region, REGIONS } from '@/constants/regions';

/**
 * Determines the selected datacenter based on query parameter, persisted value, user claims, and default.
 * Priority: query param (if valid & user has access) > persisted (if valid) > first claim > default
 *
 * @param queryParamDatacenter - Datacenter from URL query parameter
 * @param userDatacenters - Array of datacenters from Auth0 claims
 * @param defaultDatacenter - Default datacenter from environment config
 * @param persistedRegion - Previously selected region from localStorage
 * @returns Selected datacenter
 */
export function selectDatacenter(
  queryParamDatacenter: string | null | undefined,
  userDatacenters: Region[] | undefined,
  defaultDatacenter: Region,
  persistedRegion?: Region | null,
): Region {
  // Priority 0: Query parameter (if it names a known region and user has access)
  // Query param should ALWAYS take precedence (explicit user intent via URL)
  if (queryParamDatacenter) {
    if (!isRegion(queryParamDatacenter)) {
      console.warn('[Datacenter] Query param datacenter is not a known region, ignoring:', queryParamDatacenter);
    } else {
      console.log('[Datacenter] Query param datacenter provided:', queryParamDatacenter);

      // If userDatacenters not loaded yet, trust the query param (it was just set by user action).
      // It's already confirmed to be a real region above — this only skips the *access* check.
      // If userDatacenters loaded, validate access.
      if (!userDatacenters || userDatacenters.includes(queryParamDatacenter)) {
        console.log('[Datacenter] Selected from query param:', queryParamDatacenter);
        return queryParamDatacenter;
      }

      // Only log unauthorized if userDatacenters is loaded and doesn't include the region
      console.warn('[Datacenter] Query param datacenter unauthorized:', {
        requested: queryParamDatacenter,
        allowedDatacenters: userDatacenters,
      });
    }
  }

  // Priority 1: Persisted region from localStorage (if valid and user has access)
  if (persistedRegion && userDatacenters && userDatacenters.includes(persistedRegion)) {
    console.log('[Datacenter] Using persisted region:', persistedRegion);
    return persistedRegion;
  }

  // Log when persisted region is invalid or unauthorized
  if (persistedRegion && userDatacenters && !userDatacenters.includes(persistedRegion)) {
    console.warn('[Datacenter] Persisted region no longer authorized:', {
      persisted: persistedRegion,
      allowedDatacenters: userDatacenters,
    });
  }

  // Priority 2: First datacenter from claims (must be a known/supported region)
  if (userDatacenters && userDatacenters.length > 0) {
    const firstKnownRegion = userDatacenters.find((dc) => REGIONS.includes(dc));

    if (firstKnownRegion) {
      console.log('[Datacenter] Selected from claims (first):', firstKnownRegion);
      return firstKnownRegion;
    }

    console.warn('[Datacenter] No supported region found in claims, falling back to default:', {
      claimedDatacenters: userDatacenters,
      supportedRegions: REGIONS,
    });
  }

  // Priority 3: Default from environment
  console.log('[Datacenter] Using default:', defaultDatacenter);
  return defaultDatacenter;
}

/**
 * Validates if user has access to a specific datacenter
 *
 * @param datacenter - Datacenter to validate
 * @param userDatacenters - Array of datacenters from Auth0 claims
 * @returns True if user has access
 */
export function hasDatacenterAccess(
  datacenter: Region | null | undefined,
  userDatacenters: Region[] | undefined,
): boolean {
  if (!datacenter) return false;
  if (!userDatacenters || userDatacenters.length === 0) return true; // No restrictions

  return userDatacenters.includes(datacenter);
}
